// API client for SecureSend backend

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

interface ApiError {
  error: string;
  code?: string;
}

export interface ExportAttestation {
  format: 'csv' | 'jsonl';
  recordCount: number;
  generatedAt: string;
  sha256: string;
  version: string;
}

class ApiClient {
  private accessToken: string | null = null;
  private onAuthFailure: (() => void) | null = null;

  setAccessToken(token: string | null) {
    this.accessToken = token;
  }

  setOnAuthFailure(callback: () => void) {
    this.onAuthFailure = callback;
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    options: { skipAuth?: boolean; _retry?: boolean } = {}
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.accessToken && !options.skipAuth) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = (await response.json().catch(() => ({}))) as ApiError;
      const error = new Error(errorData.error || 'Request failed') as Error & {
        status: number;
        code?: string;
      };
      error.status = response.status;
      error.code = errorData.code;

      // Handle 401 with token refresh retry
      if (response.status === 401 && !options._retry && !options.skipAuth) {
        const refreshToken = localStorage.getItem('securesend_refresh_token');
        if (!refreshToken) {
          this.onAuthFailure?.();
          throw error;
        }

        try {
          // Mark as retry to prevent infinite loops
          options._retry = true;

          // Attempt token refresh
          const { session } = await this.refreshToken(refreshToken);

          // Update tokens
          localStorage.setItem('securesend_access_token', session.access_token);
          localStorage.setItem('securesend_refresh_token', session.refresh_token);
          this.accessToken = session.access_token;

          // Retry original request
          return this.request<T>(method, path, body, options);
        } catch (refreshError) {
          // Refresh failed - clear tokens and call failure callback
          localStorage.removeItem('securesend_access_token');
          localStorage.removeItem('securesend_refresh_token');
          this.onAuthFailure?.();
          throw refreshError;
        }
      }

      throw error;
    }

    // Handle empty responses
    const text = await response.text();
    if (!text) return {} as T;

    return JSON.parse(text) as T;
  }

  // Auth endpoints
  async login(email: string, password: string) {
    return this.request<{
      message: string;
      requires_mfa: boolean;
      mfa_enrolled: boolean;
      mfa_challenge_id?: string;
      session?: { access_token: string; refresh_token: string };
      user?: { name: string; role: string };
    }>('POST', '/api/auth/login', { email, password }, { skipAuth: true });
  }

  async verifyMfa(factorId: string, code: string, refreshToken: string) {
    return this.request<{
      session: { access_token: string; refresh_token: string } | null;
      user: { id: string; email: string } | null;
    }>('POST', '/api/auth/mfa/verify', { factor_id: factorId, code, refresh_token: refreshToken });
  }

  async enrollMfa() {
    return this.request<{
      factor_id: string;
      totp_uri: string;
      secret: string;
      qr_code: string;
    }>('POST', '/api/auth/mfa/enroll');
  }

  async verifyMfaEnrollment(factorId: string, code: string, refreshToken: string) {
    return this.request<{
      success: boolean;
      session: {
        access_token: string;
        refresh_token: string;
        expires_at: number;
      } | null;
    }>('POST', '/api/auth/mfa/enroll/verify', {
      factor_id: factorId,
      code,
      refresh_token: refreshToken,
    });
  }

  async requestPasswordReset(email: string) {
    return this.request<{ message: string }>(
      'POST',
      '/api/auth/password/reset-request',
      { email },
      { skipAuth: true }
    );
  }

  async sendMagicLink(email: string) {
    return this.request<{ success: boolean; message: string }>(
      'POST',
      '/api/auth/magic-link',
      { email },
      { skipAuth: true }
    );
  }

  async resetPassword(token: string, password: string) {
    return this.request<{ message: string }>(
      'POST',
      '/api/auth/password/reset',
      { token, password },
      { skipAuth: true }
    );
  }

  async changePassword(currentPassword: string, newPassword: string) {
    return this.request<{ message: string }>('POST', '/api/auth/password/change', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  }

  async logout() {
    return this.request<{ message: string }>('POST', '/api/auth/logout');
  }

  async refreshToken(refreshToken: string) {
    return this.request<{
      session: { access_token: string; refresh_token: string };
    }>('POST', '/api/auth/refresh', { refresh_token: refreshToken }, { skipAuth: true });
  }

  // User endpoints
  async getMe() {
    return this.request<{
      user: {
        id: string;
        email: string;
        name: string;
        role: 'admin' | 'user';
        must_change_password: boolean;
        notify_on_upload: boolean;
      };
      tenant: {
        name: string;
        plan: string;
        trial_ends_at?: string;
        branding_logo_url?: string;
        branding_primary_color?: string;
        branding_company_name?: string;
      };
    }>('GET', '/api/users/me');
  }

  async getUsers() {
    return this.request<{
      users: Array<{
        id: string;
        email: string;
        name: string;
        role: string;
        is_active: boolean;
        last_login_at?: string;
        created_at: string;
      }>;
    }>('GET', '/api/users');
  }

  async createUser(data: { email: string; name: string; role: 'admin' | 'user' }) {
    return this.request<{
      user: { id: string; email: string; name: string; role: string };
      temp_password: string;
    }>('POST', '/api/users', data);
  }

  async updateUser(id: string, data: { name?: string; role?: string; is_active?: boolean; notify_on_upload?: boolean }) {
    return this.request<{ user: { id: string; name: string; role: string; notify_on_upload?: boolean } }>(
      'PATCH',
      `/api/users/${id}`,
      data
    );
  }

  async updateMyPreferences(data: { notify_on_upload?: boolean }) {
    return this.request<{ success: boolean }>('PATCH', '/api/users/me/preferences', data);
  }

  async deleteUser(id: string) {
    return this.request<{ success: boolean }>('DELETE', `/api/users/${id}`);
  }

  // Folder endpoints
  async getFolders() {
    return this.request<{
      folders: Array<{
        id: string;
        name: string;
        parent_id?: string;
        depth: number;
        created_at: string;
      }>;
    }>('GET', '/api/folders');
  }

  async getFolder(id: string) {
    return this.request<{
      folder: {
        id: string;
        name: string;
        admin_notes?: string;
      };
      files: Array<{
        id: string;
        name: string;
        mime_type: string;
        size_bytes: number;
        created_at: string;
      }>;
    }>('GET', `/api/folders/${id}`);
  }

  async createFolder(data: { name: string; parent_id?: string; admin_notes?: string }) {
    return this.request<{ folder: { id: string; name: string } }>('POST', '/api/folders', data);
  }

  async updateFolder(id: string, data: { name?: string; admin_notes?: string }) {
    return this.request<{ folder: { id: string; name: string } }>('PATCH', `/api/folders/${id}`, data);
  }

  async deleteFolder(id: string) {
    return this.request<{ success: boolean }>('DELETE', `/api/folders/${id}`);
  }

  async getFolderPermissions(folderId: string) {
    return this.request<{
      permissions: Array<{
        user_id: string;
        permission: 'view' | 'upload' | 'manage';
        user: { name: string; email: string };
      }>;
    }>('GET', `/api/folders/${folderId}/permissions`);
  }

  async setFolderPermission(folderId: string, userId: string, permission: 'view' | 'upload' | 'manage') {
    return this.request<{ permission: { user_id: string; permission: string } }>(
      'POST',
      `/api/folders/${folderId}/permissions`,
      { user_id: userId, permission }
    );
  }

  async removeFolderPermission(folderId: string, userId: string) {
    return this.request<{ success: boolean }>('DELETE', `/api/folders/${folderId}/permissions/${userId}`);
  }

  // File endpoints
  async requestUpload(data: { folder_id: string; filename: string; content_type: string; size_bytes: number; expires_at?: string }) {
    return this.request<{
      file_id: string;
      upload_url: string;
      expires_in: number;
    }>('POST', '/api/files/request-upload', data);
  }

  async confirmUpload(fileId: string) {
    return this.request<{ file: { id: string; name: string } }>('POST', '/api/files/confirm-upload', {
      file_id: fileId,
    });
  }

  async getFileDownloadUrl(fileId: string) {
    // Custom handling because encrypted files return binary data directly
    // while unencrypted files return JSON with a signed URL
    const headers: Record<string, string> = {};
    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(`${API_BASE_URL}/api/files/${fileId}/download`, {
      method: 'GET',
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = (await response.json().catch(() => ({}))) as { error?: string };
      throw new Error(errorData.error || 'Failed to get download URL');
    }

    const contentType = response.headers.get('Content-Type') || '';

    // If JSON response (unencrypted files), return the download URL
    if (contentType.includes('application/json')) {
      return response.json() as Promise<{
        download_url: string;
        filename: string;
        expires_in: number;
      }>;
    }

    // If binary response (encrypted files), create a blob URL
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const filename = this.extractFilename(response.headers.get('Content-Disposition'));

    return {
      download_url: blobUrl,
      filename: filename || 'download',
      expires_in: 3600, // Blob URLs don't expire, but this is for consistency
    };
  }

  private extractFilename(contentDisposition: string | null): string {
    if (!contentDisposition) return 'download';
    // Try to extract filename from Content-Disposition header
    const match = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)/i);
    return match ? decodeURIComponent(match[1]) : 'download';
  }

  async deleteFile(fileId: string) {
    return this.request<{ success: boolean }>('DELETE', `/api/files/${fileId}`);
  }

  async searchFiles(query: string, params?: { type?: string; start_date?: string; end_date?: string }) {
    const searchParams = new URLSearchParams({ q: query });
    if (params?.type) searchParams.set('type', params.type);
    if (params?.start_date) searchParams.set('start_date', params.start_date);
    if (params?.end_date) searchParams.set('end_date', params.end_date);
    return this.request<{
      files: Array<{
        id: string;
        name: string;
        mime_type: string;
        size_bytes: number;
        folder_id: string;
        created_at: string;
        is_encrypted: boolean;
        folder?: { name: string; path: string };
        uploader?: { name: string; email: string };
      }>;
    }>('GET', `/api/files/search?${searchParams.toString()}`);
  }

  async getFilePreview(fileId: string) {
    return this.request<{
      preview_url: string;
      content_type: string;
      preview_type: 'pdf' | 'image' | 'text';
    }>('GET', `/api/files/${fileId}/preview`);
  }

  async bulkDownload(fileIds: string[]) {
    return this.request<{
      files: Array<{
        id: string;
        name: string;
        size_bytes: number;
        download_url: string;
      }>;
      total_size: number;
      expires_in: number;
    }>('POST', '/api/files/bulk-download', { file_ids: fileIds });
  }

  async updateFile(fileId: string, data: { expires_at?: string | null }) {
    return this.request<{
      file: { id: string; name: string; expires_at: string | null };
    }>('PATCH', `/api/files/${fileId}`, data);
  }

  // Share link endpoints
  async createShareLink(data: {
    file_id: string;
    password?: string;
    expires_at?: string;
    max_downloads?: number;
  }) {
    return this.request<{
      share_link: {
        id: string;
        url: string;
        token: string;
        expires_at?: string;
        max_downloads?: number;
      };
    }>('POST', '/api/share-links', data);
  }

  async getShareLinks() {
    return this.request<{
      share_links: Array<{
        id: string;
        token: string;
        file_id: string;
        download_count: number;
        max_downloads?: number;
        expires_at?: string;
        is_active: boolean;
        created_at: string;
        file: { name: string };
        creator?: { name: string; email: string };
      }>;
    }>('GET', '/api/share-links');
  }

  async revokeShareLink(id: string) {
    return this.request<{ success: boolean }>('DELETE', `/api/share-links/${id}`);
  }

  // Public share access (no auth)
  async getShareInfo(token: string) {
    return this.request<{
      file: { name: string; size_bytes: number; mime_type: string };
      requires_password: boolean;
      expires_at?: string;
      downloads_remaining?: number;
    }>('GET', `/api/share-links/access/${token}`, undefined, { skipAuth: true });
  }

  async downloadSharedFile(token: string, password?: string) {
    return this.request<{
      download_url: string;
      filename: string;
    }>('POST', `/api/share-links/access/${token}/download`, { password }, { skipAuth: true });
  }

  // Tenant endpoints
  async getTenant() {
    return this.request<{
      tenant: {
        id: string;
        name: string;
        plan: string;
        storage_used_bytes: number;
        storage_limit_bytes: number;
        branding_logo_url?: string;
        branding_primary_color?: string;
        branding_company_name?: string;
      };
      stats: {
        user_count: number;
        admin_count: number;
        file_count: number;
        folder_count: number;
        storage_used_percent: number;
      };
    }>('GET', '/api/tenant');
  }

  async updateTenant(data: { name?: string }) {
    return this.request<{ tenant: { name: string } }>('PATCH', '/api/tenant', data);
  }

  async updateBranding(data: {
    branding_logo_url?: string | null;
    branding_primary_color?: string | null;
    branding_company_name?: string | null;
  }) {
    return this.request<{
      branding: {
        branding_logo_url?: string;
        branding_primary_color?: string;
        branding_company_name?: string;
      };
    }>('PATCH', '/api/tenant/branding', data);
  }

  // Audit log endpoints
  async getAuditLogs(params?: {
    page?: number;
    limit?: number;
    category?: string;
    action?: string;
    start_date?: string;
    end_date?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.category) searchParams.set('category', params.category);
    if (params?.action) searchParams.set('action', params.action);
    if (params?.start_date) searchParams.set('start_date', params.start_date);
    if (params?.end_date) searchParams.set('end_date', params.end_date);

    const query = searchParams.toString();
    return this.request<{
      logs: Array<{
        id: string;
        action: string;
        category: string;
        user_email?: string;
        target_name?: string;
        ip_address?: string;
        success: boolean;
        created_at: string;
      }>;
      pagination: {
        page: number;
        limit: number;
        total: number;
        total_pages: number;
      };
    }>('GET', `/api/audit${query ? `?${query}` : ''}`);
  }

  async exportAuditLogs(params: {
    format: 'csv' | 'jsonl';
    category?: string;
    action?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<{ blob: Blob; attestation: ExportAttestation | null; filename: string }> {
    const searchParams = new URLSearchParams();
    searchParams.set('format', params.format);
    if (params.category) searchParams.set('category', params.category);
    if (params.action) searchParams.set('action', params.action);
    if (params.start_date) searchParams.set('start_date', params.start_date);
    if (params.end_date) searchParams.set('end_date', params.end_date);

    const headers: Record<string, string> = {};
    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(`${API_BASE_URL}/api/audit/export?${searchParams}`, {
      headers,
    });

    if (!response.ok) {
      throw new Error('Export failed');
    }

    const attestationHeader = response.headers.get('X-Export-Attestation');
    const attestation = attestationHeader ? JSON.parse(attestationHeader) : null;

    const contentDisposition = response.headers.get('Content-Disposition') || '';
    const filenameMatch = contentDisposition.match(/filename="(.+)"/);
    const filename = filenameMatch ? filenameMatch[1] : `audit-logs.${params.format}`;

    const blob = await response.blob();

    return { blob, attestation, filename };
  }

  // Billing endpoints
  async getPlans() {
    return this.request<{
      plans: Array<{
        name: string;
        storage_gb: number;
        admin_seats: number;
        max_file_size_mb: number;
      }>;
    }>('GET', '/api/billing/plans');
  }

  async createCheckout(data: {
    plan: string;
    interval: 'monthly' | 'annual';
    success_url: string;
    cancel_url: string;
  }) {
    return this.request<{ checkout_url: string }>('POST', '/api/billing/checkout', data);
  }

  async getSubscription() {
    return this.request<{
      plan: string;
      status: string;
      subscription?: {
        current_period_end: string;
        cancel_at_period_end: boolean;
      };
    }>('GET', '/api/billing/subscription');
  }

  async openBillingPortal() {
    return this.request<{ portal_url: string }>('POST', '/api/billing/portal');
  }

  // Webhook endpoints
  async getWebhooks() {
    return this.request<{
      webhooks: Array<{
        id: string;
        name: string;
        url: string;
        events: string[];
        is_active: boolean;
        created_at: string;
        updated_at: string;
        total_deliveries: number;
      }>;
    }>('GET', '/api/webhooks');
  }

  async createWebhook(data: { name: string; url: string; events: string[]; is_active?: boolean }) {
    return this.request<{
      webhook: {
        id: string;
        name: string;
        url: string;
        events: string[];
        secret: string;
        is_active: boolean;
      };
    }>('POST', '/api/webhooks', data);
  }

  async updateWebhook(id: string, data: { name?: string; url?: string; events?: string[]; is_active?: boolean }) {
    return this.request<{
      webhook: { id: string; name: string; url: string; events: string[]; is_active: boolean };
    }>('PATCH', `/api/webhooks/${id}`, data);
  }

  async deleteWebhook(id: string) {
    return this.request<{ success: boolean }>('DELETE', `/api/webhooks/${id}`);
  }

  async testWebhook(id: string) {
    return this.request<{ success: boolean; status_code?: number; error?: string }>('POST', `/api/webhooks/${id}/test`);
  }

  async regenerateWebhookSecret(id: string) {
    return this.request<{ secret: string }>('POST', `/api/webhooks/${id}/regenerate-secret`);
  }

  async getWebhookDeliveries(id: string, limit = 10) {
    return this.request<{
      deliveries: Array<{
        id: string;
        event: string;
        response_status: number;
        success: boolean;
        retry_count: number;
        attempted_at: string;
      }>;
      total: number;
    }>('GET', `/api/webhooks/${id}/deliveries?limit=${limit}`);
  }

  // API key endpoints
  async getApiKeys() {
    return this.request<{
      api_keys: Array<{
        id: string;
        name: string;
        key_prefix: string;
        scopes: string[];
        last_used_at?: string;
        expires_at?: string;
        is_active: boolean;
        created_at: string;
      }>;
    }>('GET', '/api/api-keys');
  }

  async createApiKey(data: { name: string; scopes: string[]; expires_at?: string }) {
    return this.request<{
      api_key: {
        id: string;
        name: string;
        key_prefix: string;
        scopes: string[];
        expires_at?: string;
        created_at: string;
      };
      key: string; // Full key - only returned on creation
    }>('POST', '/api/api-keys', data);
  }

  async revokeApiKey(id: string) {
    return this.request<{ success: boolean }>('DELETE', `/api/api-keys/${id}`);
  }

  // GDPR endpoints
  async deleteMyAccount(confirmEmail: string): Promise<{
    success: boolean;
    filesDeleted: number;
    foldersDeleted: number;
    auditLogsAnonymized: number;
    r2FilesDeleted: number;
  }> {
    return this.request('DELETE', '/api/gdpr/delete-account', { confirmEmail });
  }
}

export const api = new ApiClient();
